import { AxiosError } from 'axios';
import { omit } from 'lodash-es';

import { JOB_TYPE } from '../../../common/constants';
import { getFeatureEnabledForCountry } from '../../../common/hooks/useGetFeatureEnabledForCountry';
import {
  HierarchicalLocation,
  PaymentFrequency,
} from '../../../common/interfaces/entities';
import {
  BonusFrequency,
  JOB_TYPE_TO_PAYMENT_FREQUENCIES,
  JOB_TYPE_TO_SALARY_RATES,
  JobDraftFormState,
  JobFormState,
  PAYMENT_FREQUENCY_TO_SALARY_RATES,
} from './constants';

const normalizedJobFormState = (
  jobFormState: Partial<JobFormState | JobDraftFormState>
) => ({
  ...omit(jobFormState, [
    'descriptionRaw',
    'interviewProcessRaw',
    'isEdit',
    'isCopy',
    'initializeRememberedCompanyAddress',
    'jobDraftId',
    'addBonus',
    'HierarchicalJobCategoryName',
    'countryName',
    'districtOptions',
    'step',
    'shouldShowMatchCriteria',
    'keywordMatchCriteria.questions',
    'userChangedJobType',
    'jobTypeSuggestionTitle',
  ]),
  description: jobFormState.description?.getPlainText().trim() || '',
  interviewProcess: jobFormState.interviewProcess?.getPlainText().trim() || '',
  shouldPublishOnLinkedin: jobFormState.shouldPublishOnLinkedin ?? false,
});

export const compareJobFormState = (
  originalFormState: Partial<JobFormState | JobDraftFormState>,
  modifiedFormState: Partial<JobFormState | JobDraftFormState>
) => {
  const normalizedA = normalizedJobFormState(originalFormState);
  const normalizedB = normalizedJobFormState(modifiedFormState);

  return JSON.stringify(normalizedA) === JSON.stringify(normalizedB);
};

export const checkIsNonDefaultVIPValueUsedError = (
  error: Error | undefined
): boolean => {
  if (!error) return false;

  return (
    (error as AxiosError<{ error: { code: string } }>)?.response?.data.error
      ?.code === 'NonDefaultVIPValueByNonVIPCompanyError'
  );
};

export const isWorkAddressEnabledForJob = (
  isFeatureFlagEnabled: boolean,
  countryCode: string
) =>
  isFeatureFlagEnabled &&
  getFeatureEnabledForCountry(countryCode).isWorkAddressEnabled;

export const getHierarchicalLocationPrefill = (
  jobLocation: HierarchicalLocation | null | undefined,
  districtId?: string | null
) =>
  districtId
    ? {
        districtLocation: omit(jobLocation, 'parents'),
        cityLocation: jobLocation?.parents?.[0] ?? null,
        provinceLocation: jobLocation?.parents?.[1] ?? null,
      }
    : {
        districtLocation: null,
        cityLocation: omit(jobLocation, 'parents'),
        provinceLocation: jobLocation?.parents?.[0] ?? null,
        districtOptions: jobLocation?.children,
      };

export const sanitizeSalaryByCommitmentType = (
  jobType: JOB_TYPE,
  paymentFrequency: PaymentFrequency | null | undefined,
  salaryRate: BonusFrequency | string | null | undefined
): {
  paymentFrequency: PaymentFrequency | null;
  salaryRate: BonusFrequency | null;
} => {
  if (!paymentFrequency) {
    return {
      paymentFrequency: null,
      salaryRate: (salaryRate ?? null) as BonusFrequency | null,
    };
  }

  const allowedFreqs = JOB_TYPE_TO_PAYMENT_FREQUENCIES[jobType];
  if (!allowedFreqs.includes(paymentFrequency)) {
    return { paymentFrequency: null, salaryRate: null };
  }

  const salaryRateMap =
    JOB_TYPE_TO_SALARY_RATES[jobType] ?? PAYMENT_FREQUENCY_TO_SALARY_RATES;
  const allowedRates = salaryRateMap[paymentFrequency];
  if (salaryRate && !allowedRates.includes(salaryRate as BonusFrequency)) {
    return { paymentFrequency, salaryRate: allowedRates[0] };
  }

  return {
    paymentFrequency,
    salaryRate: (salaryRate ?? null) as BonusFrequency | null,
  };
};
